import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/status-badge";
import { Timeline } from "@/components/shared/timeline";
import { postApiExecution } from "@/lib/api/generated/workflows/workflows";
import { queryKeys } from "@/lib/query/keys";
import { formatTimestamp } from "@/lib/utils/formatting";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";

export function ExecutionDetailPage() {
  const { executionId } = useParams<{ executionId: string }>();
  const { data: exec } = useQuery({
    queryKey: queryKeys.execution(executionId!),
    queryFn: () => postApiExecution({ executionId: executionId! }),
    enabled: Boolean(executionId),
  });
  useDocumentTitle(exec?.executionId ? `Execution ${exec.executionId.slice(0, 12)}` : "Execution");

  if (!exec) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>Execution not found.</p>
      </div>
    );
  }

  const timelineEvents = exec.auditTrail.map((event) => ({
    id: event.id,
    title: event.eventType,
    timestamp: event.createdAt,
    description: event.actor.displayName ?? event.actor.actorId,
    status: event.eventType.toLowerCase().includes("fail") ? "failed" : undefined,
  }));
  const failedEventDetails = exec.auditTrail
    .filter((event) => event.eventType.toLowerCase().includes("fail"))
    .at(-1)?.details;

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold font-mono" style={{ color: "var(--text-strong)" }}>
          {exec.executionId}
        </h1>
        <StatusBadge status={exec.status} />
      </div>

      <Card>
        <CardHeader><CardTitle>Details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Workflow</span>
              <p style={{ color: "var(--text-strong)" }}>{exec.workflowKey}</p>
            </div>
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Tenant</span>
              <p className="font-mono" style={{ color: "var(--text-strong)" }}>{exec.tenantId}</p>
            </div>
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Engine</span>
              <p style={{ color: "var(--text-strong)" }}>{exec.engineKind}</p>
            </div>
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Conversation</span>
              <p className="font-mono" style={{ color: "var(--text-strong)" }}>{exec.conversationId}</p>
            </div>
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Started</span>
              <p style={{ color: "var(--text-strong)" }}>{formatTimestamp(exec.startedAt)}</p>
            </div>
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Completed</span>
              <p style={{ color: "var(--text-strong)" }}>{formatTimestamp(exec.completedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Input</CardTitle></CardHeader>
        <CardContent>
          <pre className="text-xs font-mono p-3 rounded-[var(--radius-sm)] overflow-x-auto" style={{ background: "var(--bg-elevated)", color: "var(--text)" }}>
            {JSON.stringify(exec.input, null, 2)}
          </pre>
        </CardContent>
      </Card>

      {exec.status === "failed" && (exec.result !== undefined || failedEventDetails !== undefined) && (
        <Card>
          <CardHeader><CardTitle>Error</CardTitle></CardHeader>
          <CardContent>
            <pre
              className="text-xs font-mono p-3 rounded-[var(--radius-sm)] overflow-x-auto"
              style={{ background: "rgba(239, 68, 68, 0.08)", color: "var(--danger)" }}
            >
              {JSON.stringify(exec.result ?? failedEventDetails, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {exec.status !== "failed" && exec.result !== undefined && (
        <Card>
          <CardHeader><CardTitle>Result</CardTitle></CardHeader>
          <CardContent>
            <pre className="text-xs font-mono p-3 rounded-[var(--radius-sm)] overflow-x-auto" style={{ background: "var(--bg-elevated)", color: "var(--text)" }}>
              {JSON.stringify(exec.result, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Audit Trail</CardTitle></CardHeader>
        <CardContent>
          {exec.auditTrail.length === 0 ? (
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>No audit events recorded.</p>
          ) : (
            <Timeline events={timelineEvents} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
